<template>
  <div class="ec-list">

    <admin-table-subheader
      v-model="search"
      title="Licence Groups"
      search
      search-placeholder="Search group…"
      :refresh-button="true"
      :refresh-loading="loading"
      :primary-button="{ label: 'New Group', icon: 'mdi-plus', click: openCreateDialog }"
      @refresh="fetchGroups"
    />

    <div v-if="loading" class="ec-loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <v-data-table
      v-else
      :headers="headers"
      :items="groups"
      :search="search"
      :items-per-page="30"
      :footer-props="{ 'items-per-page-options': [10, 30, 50, -1] }"
      class="ec-table"
    >
      <template v-slot:[`item.label`]="{ item }">
        <div style="display:flex;align-items:center;gap:8px">
          <v-icon size="16" :color="getColor(item.label)">
            {{ item.icon || 'mdi-folder-outline' }}
          </v-icon>
          <code class="lg-name">{{ item.label }}</code>
        </div>
      </template>

      <template v-slot:[`item.description`]="{ item }">
        <span class="u-date" style="max-width:250px;display:inline-block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
          {{ item.description || '—' }}
        </span>
      </template>

      <template v-slot:[`item.active`]="{ item }">
        <span class="u-status-pill" :class="item.active ? 'u-pill--green' : 'u-pill--red'">
          <v-icon size="11" style="margin-right:3px">{{ item.active ? 'mdi-check' : 'mdi-close' }}</v-icon>
          {{ item.active ? 'Active' : 'Passive' }}
        </span>
      </template>

      <template v-slot:[`item.children_count`]="{ item }">
        <span class="ec-count-pill ec-count-pill--neutral">
          {{ item.children_count }} sub-licence{{ item.children_count !== 1 ? 's' : '' }}
        </span>
      </template>

      <template v-slot:[`item.children`]="{ item }">
        <div v-if="item.children && item.children.length > 0" style="display:flex;flex-wrap:wrap;gap:4px">
          <span
            v-for="child in item.children.slice(0, 3)"
            :key="child.id"
            class="ec-badge ec-badge--gray"
            style="font-size:.68rem"
          >{{ child.label }}</span>
          <span v-if="item.children.length > 3" class="ec-badge ec-badge--gray" style="font-size:.68rem">
            +{{ item.children.length - 3 }} more
          </span>
        </div>
        <span v-else class="u-status-pill u-pill--gray">None</span>
      </template>

      <template v-slot:[`item.created_at`]="{ item }">
        <span class="u-date">{{ item.created_at ? new Date(item.created_at).toLocaleDateString() : '—' }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <button class="ec-icon-btn ec-icon-btn--primary" title="Assign Licences" @click="openAssignDialog(item)">
          <v-icon size="15">mdi-link-variant-plus</v-icon>
        </button>
        <button class="ec-icon-btn ec-icon-btn--info" title="Edit Group" @click="openEditDialog(item)">
          <v-icon size="15">mdi-pencil-outline</v-icon>
        </button>
        <button class="ec-icon-btn ec-icon-btn--danger" title="Delete Group" @click="deleteGroup(item)">
          <v-icon size="15">mdi-delete-outline</v-icon>
        </button>
      </template>
    </v-data-table>

    <!-- ── Create / Edit Dialog ─────────────────────────────────────────── -->
    <v-dialog v-model="dialog" persistent max-width="560" content-class="ec-dialog">
      <v-form ref="groupForm" v-model="formValid" lazy-validation>
        <div class="ec-dialog__header">
          <h3 class="ec-dialog__title">{{ isEdit ? 'Edit' : 'New' }} Licence Group</h3>
          <button class="ec-icon-btn" @click="closeDialog">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="ec-dialog__body">
          <div class="ec-field">
            <label class="ec-label">Group Name *</label>
            <v-text-field
              v-model="form.label"
              :rules="[rules.required]"
              placeholder="e.g. AI Platform, Application Hub"
              outlined dense hide-details="auto"
            />
          </div>

          <div class="ec-field">
            <label class="ec-label">Description</label>
            <v-textarea
              v-model="form.description"
              placeholder="Description of this licence group"
              outlined dense rows="3" hide-details
            />
          </div>

          <div class="ec-field">
            <label class="ec-label">Icon</label>
            <v-autocomplete
              v-model="form.icon"
              :items="iconOptions"
              outlined dense hide-details
            >
              <template v-slot:item="{ item }">
                <v-icon class="mr-3">{{ item }}</v-icon>
                <span>{{ item }}</span>
              </template>
              <template v-slot:selection="{ item }">
                <v-icon class="mr-2">{{ item }}</v-icon>
                <span>{{ item }}</span>
              </template>
            </v-autocomplete>
          </div>

          <v-switch
            v-if="isEdit"
            v-model="form.active"
            label="Active"
            color="green"
            dense hide-details
          />
        </div>

        <div class="ec-dialog__footer">
          <button class="ec-btn ec-btn--ghost" type="button" @click="closeDialog">Cancel</button>
          <button
            class="ec-btn ec-btn--primary"
            type="button"
            :disabled="!formValid || saving"
            @click="saveGroup"
          >
            <v-progress-circular v-if="saving" size="13" width="2" indeterminate style="margin-right:6px" />
            {{ isEdit ? 'Update' : 'Create' }}
          </button>
        </div>
      </v-form>
    </v-dialog>

    <!-- ── Assign Licences Dialog ────────────────────────────────────────── -->
    <assign-licences-dialog
      v-model="assignDialog"
      :group="assignGroup"
      :all-groups="groups"
      @saved="fetchGroups"
    />
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import { apiService } from '../../service/api.service'
import AssignLicencesDialog from './AssignLicencesDialog.vue'
import AdminTableSubheader from '../shared/AdminTableSubheader.vue'

export default {
  name: 'LicenceGroups',
  components: { AssignLicencesDialog, AdminTableSubheader },
  data () {
    return {
      groups: [],
      loading: false,
      saving: false,
      search: null,
      dialog: false,
      isEdit: false,
      editId: null,
      formValid: true,
      form: {
        label: '',
        description: '',
        icon: 'mdi-folder-outline',
        active: true
      },
      rules: {
        required: (v) => !!v || 'This field is required'
      },
      iconOptions: [
        'mdi-folder-outline', 'mdi-chip', 'mdi-application', 'mdi-robot-outline',
        'mdi-cloud-outline', 'mdi-server', 'mdi-monitor-dashboard', 'mdi-cube-outline',
        'mdi-memory', 'mdi-video-outline', 'mdi-camera-outline', 'mdi-shield-check-outline',
        'mdi-cog-outline', 'mdi-database-outline', 'mdi-sitemap-outline', 'mdi-puzzle-outline',
        'mdi-layers-outline', 'mdi-lightning-bolt-outline', 'mdi-brain', 'mdi-eye-outline'
      ],
      headers: [
        { text: 'Group Name', value: 'label', align: 'start' },
        { text: 'Description', value: 'description', align: 'start' },
        { text: 'Status', value: 'active', align: 'center' },
        { text: 'Sub-Licences', value: 'children_count', align: 'center' },
        { text: 'Children', value: 'children', align: 'start' },
        { text: 'Created', value: 'created_at', align: 'center' },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false }
      ],
      assignDialog: false,
      assignGroup: {}
    }
  },
  mounted () { this.fetchGroups() },
  methods: {
    getColor (label) {
      const colors = {
        'AI Platform': 'blue darken-1',
        'Application Hub': 'deep-purple darken-1'
      }
      return colors[label] || 'teal darken-1'
    },

    async fetchGroups () {
      this.loading = true
      try {
        const { data } = await apiService.get('admin-parent-licences')
        this.groups = data.data || []
      } catch (err) {
        console.error('Error fetching licence groups:', err)
        Swal.fire('Error!', err.response?.data?.message || 'Failed to load licence groups', 'error')
      } finally {
        this.loading = false
      }
    },

    openCreateDialog () {
      this.isEdit = false
      this.editId = null
      this.form = { label: '', description: '', icon: 'mdi-folder-outline', active: true }
      this.dialog = true
    },

    openEditDialog (item) {
      this.isEdit = true
      this.editId = item.id
      this.form = { label: item.label, description: item.description || '', icon: item.icon || 'mdi-folder-outline', active: item.active }
      this.dialog = true
    },

    closeDialog () {
      this.dialog = false
      if (this.$refs.groupForm) this.$refs.groupForm.reset()
    },

    async saveGroup () {
      if (!this.$refs.groupForm.validate()) return
      this.saving = true
      try {
        if (this.isEdit) {
          await apiService.put(`admin-update-parent-licence/${this.editId}`, this.form)
          Swal.fire('Updated!', 'Licence group has been updated.', 'success')
        } else {
          await apiService.post('admin-create-parent-licence', this.form)
          Swal.fire('Created!', 'Licence group has been created.', 'success')
        }
        this.closeDialog()
        this.fetchGroups()
      } catch (err) {
        Swal.fire('Error!', err.response?.data?.message || 'An error occurred', 'error')
      } finally {
        this.saving = false
      }
    },

    async deleteGroup (item) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        html: `<p>The licence group <b>"${item.label}"</b> will be deleted.</p>
               <p class="text-muted">Its ${item.children_count} sub-licence(s) will become standalone licences.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        confirmButtonColor: '#d33'
      })
      if (result.value) {
        try {
          await apiService.delete(`admin-delete-parent-licence/${item.id}`)
          Swal.fire('Deleted!', 'Licence group has been deleted.', 'success')
          this.fetchGroups()
        } catch (err) {
          Swal.fire('Error!', err.response?.data?.message || 'An error occurred', 'error')
        }
      }
    },

    openAssignDialog (group) {
      this.assignGroup = group
      this.assignDialog = true
    }
  }
}
</script>

<style scoped>
.lg-name {
  font-size: .8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
  color: var(--ec-text-1);
  background: var(--ec-gray-bg);
  padding: 2px 7px;
  border-radius: 4px;
}
</style>
